import type { SvgIconName } from '@components/Svg';
import type { BaseUser, User } from '@root/@types/types';
import { UserRole } from '@root/@types/types';
import type { FeatureFlag } from '@root/helpers/feature-flags';
import { getUserRole } from '@root/helpers/permissions/utils';

import Nav, { config } from '../routes';

type Menu = {
  path: Nav;
  icon: SvgIconName;
  allowedRoles?: string[];
  featureFlag?: FeatureFlag;
};

export type MenuNavigation = Pick<Menu, 'path' | 'icon'> & {
  name: string;
};

export const getMenu = (): Menu[] => [
  {
    path: Nav.Submissions,
    icon: 'submissions',
    allowedRoles: [UserRole.Admin, UserRole.Superadmin, UserRole.User],
  },
  {
    path: Nav.Templates,
    icon: 'templates',
    allowedRoles: [UserRole.Admin, UserRole.Superadmin, UserRole.TemplateManager],
  },
  { path: Nav.Blocks, icon: 'blocks', allowedRoles: [UserRole.Admin, UserRole.Superadmin, UserRole.TemplateManager] },
  { path: Nav.Clauses, icon: 'clauses', allowedRoles: [UserRole.Admin, UserRole.Superadmin, UserRole.TemplateManager] },
  {
    path: Nav.Branding,
    icon: 'photo',
    featureFlag: 'Branding',
    allowedRoles: [UserRole.Admin, UserRole.Superadmin, UserRole.TemplateManager],
  },
  { path: Nav.Tags, icon: 'tags', allowedRoles: [UserRole.Admin, UserRole.Superadmin, UserRole.TemplateManager] },
  { path: Nav.Users, icon: 'users', allowedRoles: [UserRole.Superadmin], featureFlag: 'Users' },
  {
    path: Nav.EvalConfigs,
    icon: 'settings',
    allowedRoles: [UserRole.Superadmin],
    featureFlag: 'EvalConfigs',
  },
];

export const createMenuForUser = (
  user: User | BaseUser | null,
  getFeatureFlagState: (featureFlag: FeatureFlag) => boolean | undefined,
): MenuNavigation[] => {
  if (!user) {
    return [];
  }

  const userRole = getUserRole(user);

  if (!userRole) {
    return [];
  }

  const menu = getMenu();

  const filtered = menu.filter(({ allowedRoles, featureFlag }) => {
    if (featureFlag && !getFeatureFlagState(featureFlag)) {
      return false;
    }

    if (!allowedRoles) {
      return true;
    }

    return allowedRoles.includes(userRole);
  });

  return filtered.map(({ path, icon }) => ({ path, icon, name: config[path].title }));
};
